import { AnswerSheetSidebarShimmer } from "../quizzes/AnswerSheetShimmer";
import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import AnswerTabs from "../quizzes/AnswerTabs";
import { useGamificationAnswerSheet } from "../../hooks/useGamificationAnswerSheet";
import { useSidebarData } from "../../hooks/useSidebarData";

import {
  buildGamificationBackTarget,
  buildGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./gamificationFlowState";

import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "../quizzes/AnswerSheets.css";

import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";

const GamificationAnswerSheets = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeAnswerTab, setActiveAnswerTab] = useState("all");

  const { data: sidebarData = null } = useSidebarData();

  const flowState = useMemo(
    () =>
      buildGamificationFlowState(
        location.state,
        getStoredGamificationFlowState(),
      ),
    [location.state],
  );

  const selectedAttempt = flowState?.selectedAttempt || {};

  const battlesId = Number(
    selectedAttempt?.battlesId ||
      selectedAttempt?.battles_id ||
      selectedAttempt?.raw?.battles_id ||
      flowState?.selectedBattle?.battles_id ||
      flowState?.selectedBattle?.battle_id ||
      0,
  );

  const battleRoomId = Number(
    flowState?.battle_room_id ||
      selectedAttempt?.battleRoomId ||
      selectedAttempt?.battle_room_id ||
      selectedAttempt?.raw?.battle_room_id ||
      0,
  );

  const resolvedBackTarget = useMemo(
    () =>
      flowState?.gamificationBackTarget?.pathname
        ? flowState.gamificationBackTarget
        : buildGamificationBackTarget(GAMIFICATION_ROUTES.start, flowState),
    [flowState],
  );

  const answerSheetScreenState = useMemo(
    () =>
      buildGamificationFlowState({
        ...flowState,
        gamificationBackTarget: resolvedBackTarget,
      }),
    [flowState, resolvedBackTarget],
  );

  const leaderboardNavigationState = useMemo(
    () =>
      buildGamificationFlowState({
        ...answerSheetScreenState,
        battles_id: battlesId,
        battle_room_id: battleRoomId,
        gamificationBackTarget: buildGamificationBackTarget(
          GAMIFICATION_ROUTES.answerSheet,
          answerSheetScreenState,
        ),
      }),
    [answerSheetScreenState, battleRoomId, battlesId],
  );

  useEffect(() => {
    persistGamificationFlowState(answerSheetScreenState);
  }, [answerSheetScreenState]);

  const goBack = () => {
    navigate(resolvedBackTarget.pathname, {
      state: resolvedBackTarget.state,
      replace: true,
    });
  };

  /* API */

  const answerSheetQuery = useGamificationAnswerSheet(
    {
      battles_id: battlesId,
      battle_room_id: battleRoomId,
      states_id: Number(
        flowState?.state_id || flowState?.selectedState?.state_id || 0,
      ),
      subject_id: Number(
        flowState?.subject_id || flowState?.selectedSubject?.subject_id || 0,
      ),
    },
    {
      enabled: battlesId > 0 && battleRoomId > 0,
    },
  );

  /* STATS */

  const answerStats = useMemo(
    () => [
      {
        value: answerSheetQuery.data?.attempted || "0/0",
        label: "Attempted",
      },
      {
        value: answerSheetQuery.data?.rank || "-",
        label: "Rank",
        route: GAMIFICATION_ROUTES.battleLeaderboard,
      },
      {
        value: answerSheetQuery.data?.timeTaken || "00:00:00",
        label: "Time Taken",
      },
    ],
    [answerSheetQuery.data],
  );

  const profile = sidebarData?.user_data || {};

  const userName = profile?.name || profile?.user_name || "Student";

  /* QUESTIONS */

  const hasAnswerSheet = Boolean(answerSheetQuery.data) && !answerSheetQuery.isPlaceholderData;
  const isInitialLoading = !hasAnswerSheet && (answerSheetQuery.isLoading || answerSheetQuery.isFetching || answerSheetQuery.isPlaceholderData);
  const answerQuestions = answerSheetQuery.data?.questions || [];

  /* FILTER COUNTS */

  const answerCounts = {
    all: answerQuestions.length,
    correct: answerQuestions.filter((question) => question.status === "correct")
      .length,
    incorrect: answerQuestions.filter(
      (question) => question.status === "incorrect",
    ).length,
    unsolved: answerQuestions.filter(
      (question) => question.status === "unsolved",
    ).length,
  };

  const answerFilters = [
    {
      id: "all",
      icon: "bi-list-check",
    },
    {
      id: "correct",
      icon: "bi-check-circle",
    },
    {
      id: "incorrect",
      icon: "bi-x-circle",
    },
    {
      id: "unsolved",
      icon: "bi-arrow-repeat",
    },
  ];

  /* BACK BUTTON HANDLING */

  useEffect(() => {
    window.history.pushState(
      { gamificationAnswerSheetGuard: true },
      "",
      window.location.href,
    );

    const handlePopState = () => {
      goBack();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [resolvedBackTarget]);

  return (
    <>
      <style>{`
          .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
       .homeDashboard-wrapper .main-content{
         height: 100vh;
         overflow: scroll;
         box-sizing: border-box;
         padding-bottom: 35px;
         min-height: 0;
         overflow-x: hidden !important;
       }
      .quizInstruction-Btn {
        bottom: 0px;
      }
     @media (max-width: 767px){
      .homeDashboard-wrapper .main-content {
         height: calc(100vh - 0px) !important;
         padding-bottom: 0px!important;
      }
      .instruction-main {
        min-height: calc(100vh - 0px);
        height: calc(100vh - 0px);
     }
      }
     `}</style>

      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding fixed-page-btn">
          <div className="start-quiz qbl-main instruction-main">
            {/* PAGE HEADER */}

            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={goBack}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>

                <div>
                  <h4 className="d-flex categories-page-title">Answer Sheet</h4>
                </div>

                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {/* MAIN LAYOUT */}

            <div className="section-divide answerSheet-layout" aria-busy={isInitialLoading}>
              {/* LEFT SIDEBAR */}

              <div className="answerSheet-sidebar">
                {isInitialLoading ? <AnswerSheetSidebarShimmer actionCount={1} /> : <div className="answerSheet-sidebarTop">
                  <h1 className="keep-title">Keep Trying, {userName}</h1>

                  <div className="keep-label">
                    Keep Practice, Keep Improving
                  </div>

                  {/* STATS */}

                  <div className="quizAnswer-stats">
                    {answerStats.map((stat) => (
                      <div className="answerSheet-statRow" key={stat.label}>
                        <span className="answerSheet-statLabel">
                          {stat.label}
                        </span>

                        {stat.route ? (
                          <button
                            type="button"
                            className="answerSheet-statValue answerSheet-rankLink"
                            onClick={() =>
                              navigate(stat.route, {
                                state: leaderboardNavigationState,
                              })
                            }
                          >
                            {stat.value}
                          </button>
                        ) : (
                          <strong className="answerSheet-statValue">
                            {stat.value}
                          </strong>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* NAVIGATION BUTTONS */}

                  <div className="answerSheet-sidebarButtons">
                    <button
                      type="button"
                      className="answerSheet-blueBtn"
                      onClick={() =>
                        navigate(GAMIFICATION_ROUTES.battleLeaderboard, {
                          state: leaderboardNavigationState,
                        })
                      }
                    >
                      <i className="bi bi-trophy-fill" />
                      <span>Leaderboard</span>
                    </button>
                  </div>

                  {/* FILTERS */}

                  <div className="answerSheet-filterSection">
                    <h3 className="answerSheet-filterTitle">Filters</h3>

                    <div className="answerSheet-filterGrid">
                      {answerFilters.map((filter) => (
                        <button
                          key={filter.id}
                          type="button"
                          className={`
                            answerSheet-filterBtn
                            answerSheet-filter-${filter.id}
                            ${activeAnswerTab === filter.id ? "active" : ""}
                          `}
                          onClick={() => setActiveAnswerTab(filter.id)}
                          aria-pressed={activeAnswerTab === filter.id}
                        >
                          <i className={`bi ${filter.icon}`} />
                          <span>{answerCounts[filter.id]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>}

                {/* REFER & EARN BANNER */}

                {/* <div className="answerSheet-referBanner">
                
                </div> */}
              </div>

              {/* RIGHT QUESTION SECTION */}

              <div className="answerSheet-content">
                {answerSheetQuery.isError && !hasAnswerSheet ? (
                  <div className="quiz-empty mt-3">
                    <i className="bi bi-exclamation-circle" />

                    <h3>Unable to load answer sheet</h3>

                    <p>
                      {answerSheetQuery.error?.message || "Please try again."}
                    </p>
                  </div>
                ) : (
                  <AnswerTabs
                    questions={answerQuestions}
                    isLoading={isInitialLoading}
                    activeTab={activeAnswerTab}
                    onTabChange={setActiveAnswerTab}
                    hideTabs={true}
                  />
                )}
              </div>
            </div>
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default GamificationAnswerSheets;
